package com.crowpay.views.screens.contractor

import com.crowpay.AccountInfo
import com.crowpay.Contractor
import com.crowpay.Formats
import com.crowpay.PaymentState
import com.crowpay.PendingCashOut
import com.crowpay._id
import com.crowpay.views.components.nav.AppDimensions
import com.crowpay.amount
import com.crowpay.contractor
import com.crowpay.created
import com.crowpay.format
import com.crowpay.notNullContractor
import com.crowpay.intentId
import com.crowpay.project
import com.crowpay.sdk.currentSession
import com.crowpay.sdk.notNullSession
import com.crowpay.state
import com.crowpay.utils.Action
import com.crowpay.utils.JumpTo
import com.crowpay.utils.existsDefaultFalse
import com.crowpay.utils.renderDollars
import com.crowpay.views.components.expandAction
import com.crowpay.views.components.expandIcon
import com.crowpay.views.components.sectionIndentCol
import com.crowpay.views.dialogs.CollectFundsDialog
import com.crowpay.views.theming.AppColors
import com.crowpay.views.theming.BackgroundSemantic
import com.crowpay.views.theming.ForegroundSemantic
import com.crowpay.views.theming.LightSection
import com.crowpay.views.theming.TransparentBackgroundSemantic
import com.crowpay.views.theming.body
import com.crowpay.views.theming.bodyBold
import com.crowpay.views.theming.centeredBody
import com.crowpay.views.theming.colored
import com.crowpay.views.theming.lightSection
import com.crowpay.views.theming.smallBody
import com.crowpay.views.theming.subTitle
import com.crowpay.views.theming.textButton
import com.crowpay.views.theming.title
import com.lightningkite.UUID
import com.lightningkite.kiteui.models.Align
import com.lightningkite.kiteui.models.Color
import com.lightningkite.kiteui.models.Icon
import com.lightningkite.kiteui.models.px
import com.lightningkite.kiteui.models.rem
import com.lightningkite.kiteui.navigation.dialogScreenNavigator
import com.lightningkite.kiteui.navigation.screenNavigator
import com.lightningkite.kiteui.reactive.LateInitProperty
import com.lightningkite.kiteui.reactive.Property
import com.lightningkite.kiteui.reactive.Readable
import com.lightningkite.kiteui.reactive.awaitNotNull
import com.lightningkite.kiteui.reactive.invoke
import com.lightningkite.kiteui.reactive.shared
import com.lightningkite.kiteui.views.ViewWriter
import com.lightningkite.kiteui.views.affirmative
import com.lightningkite.kiteui.views.bold
import com.lightningkite.kiteui.views.buttonTheme
import com.lightningkite.kiteui.views.centered
import com.lightningkite.kiteui.views.direct.button
import com.lightningkite.kiteui.views.direct.col
import com.lightningkite.kiteui.views.direct.gravity
import com.lightningkite.kiteui.views.direct.onClick
import com.lightningkite.kiteui.views.direct.onlyWhen
import com.lightningkite.kiteui.views.direct.row
import com.lightningkite.kiteui.views.direct.sizeConstraints
import com.lightningkite.kiteui.views.direct.space
import com.lightningkite.kiteui.views.expanding
import com.lightningkite.kiteui.views.forEach
import com.lightningkite.kiteui.views.l2.icon
import com.lightningkite.lightningdb.Query
import com.lightningkite.lightningdb.and
import com.lightningkite.lightningdb.condition
import com.lightningkite.lightningdb.eq
import com.lightningkite.lightningdb.inside
import com.lightningkite.lightningdb.lt
import com.lightningkite.lightningdb.sort
import kotlinx.coroutines.launch
import kotlin.collections.map

abstract class ContractorPaymentInfo {

    private var _contractor: Readable<Contractor>? = null
    val ViewWriter.contractor
        get() = _contractor ?: run {
            _contractor = shared {
                notNullSession().contractors[notNullContractor<UUID>()]()!!
            }
            _contractor!!
        }

    val pendingCash = LateInitProperty<PendingCashOut>()
    val paymentInfo = LateInitProperty<AccountInfo?>()


    suspend fun ViewWriter.retrievePaymentPendingCash() {
        val c = contractor.awaitNotNull()
        val result = currentSession.awaitNotNull().nonCached.contractor.pendingCashOut(c._id)
        pendingCash.set(result)
    }

    suspend fun ViewWriter.retrievePaymentInfo() {
        val c = contractor.awaitNotNull()
        val result = currentSession.awaitNotNull().nonCached.contractor.getPaymentInfo(c._id)
        paymentInfo.set(result)
    }

    // retrieve relevant projectLedgerItems
    val projects = shared {
        val session = notNullSession()
        val membership = session.self().membership ?: return@shared listOf()

        session.projects.query(
            Query(
                condition {
                    it.contractor eq membership.contractor
                },
                limit = Int.MAX_VALUE
            )
        )()
    }

    val pageSize = 20
    val transferPage = Property(0)

    val newAvailableFunds = shared {
        notNullSession().projectLedgerItems.query(
            Query(
                condition {
                    (it.project inside projects().map { it._id }) and
                            (it.contractor eq true) and
                            (it.state eq PaymentState.AwaitingConfirmation) and
                            (it.intentId eq null)
                },
                orderBy = sort { it.created.descending() }
            )
        )()
    }

    val newAvailableFundsGroupedByProject = shared {
        notNullSession().projectLedgerItems.query(
            Query(
                condition {
                    (it.project inside projects().map { it._id }) and
                            (it.contractor eq true) and
                            (it.state eq PaymentState.AwaitingConfirmation) and
                            (it.intentId eq null)
                },
                orderBy = sort { it.created.descending() }
            )
        )()
            .groupBy { it.project }.entries.map {
                val project = projects().find { project -> project._id == it.key }
                Pair(project, it.value)
            }
    }

    val ledgerItemCount = shared {
        notNullSession().projectLedgerItems.watch(
            Query(
                condition {
                    (it.project inside projects().map { it._id }) and (it.amount lt 0)
                },
                orderBy = sort { it.created.descending() },
                limit = Int.MAX_VALUE
            )
        )().count()
    }

    val ledgerItems = shared {
        notNullSession().projectLedgerItems.query(
            Query(
                condition {
                    (it.project inside projects().map { it._id }) and (it.amount lt 0)
                },
                orderBy = sort { it.created.descending() },
                limit = Int.MAX_VALUE
//                limit = pageSize,
//                skip = pageSize * transferPage()
            ),
        )()
    }

    val lineItems = shared {
        notNullSession().lineItems.query(
            Query(
                condition {
                    it._id inside ledgerItems().mapNotNull { it.lineItem }
                },
                orderBy = sort { it.created.descending() },
                limit = Int.MAX_VALUE
            ),
        )()
    }

    // views
    fun ViewWriter.availableFundsCard() = lightSection - col {
        spacing = AppDimensions.sectionIndent
        launch { retrievePaymentPendingCash() }
        col {
            space(0.0)
            subTitle("Available Funds")
            colored(AppColors.secondary.main) - bold - title {
                ::content{ pendingCash().available.renderDollars() }
            }
            buttonTheme - affirmative - button {
                ::enabled{ pendingCash().available > 0 }
                centered - bodyBold("Collect Funds")
                onClick {
                    dialogScreenNavigator.navigate(
                        CollectFundsDialog(
                            amount = pendingCash().available
                        )
                    )
                }
            }
        }
    }

    fun ViewWriter.newAvailableFundsTable() = lightSection - col {
        spacing = AppDimensions.sectionIndent
        space(0.0)
        title("New Available Funds")
        smallBody("*CrowPay receives a % service charge")
        space(0.0)

        // todo: The current way the newAvailableDraws are being shown will not be pretty if there are many rows
        // todo: Alex is getting feedback on how to display this section

//        val rows = shared {
//            newAvailableFundsGroupedByProject()
//        }
//
//        col {
//            spacing = AppDimensions.buttonSpacing
//            forEach(rows) { (project, ledgerItems) ->
//                col {
//                    spacing = AppDimensions.expandButtonSpace
//                    // project header button
//                    val expand = Property(false)
//                    col {
//                        spacing = 0.px
//                        sizeConstraints(height = 3.rem) - row {
//                            expanding - button {
//                                spacing = 0.px
//                                row {
//                                    spacing = AppDimensions.expandButtonSpace
//                                    centered - expandIcon(expand)
//                                    expanding - compact - row {
//                                        centered - body { ::content { project?.name ?: "[project name]" } }
//                                        expanding - space()
//                                        sizeConstraints(minWidth = 6.rem) - centered - centeredBody("Available To Transfer")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody(
//                                            ledgerItems.sumOf { it.amount }.renderDollars(
//                                                true
//                                            )
//                                        )
//                                    }
//                                }
//                                action = expandAction(expand)
//                            }
//                        }
//                    }
//                    onlyWhen { expand() } - col {
//                        val items = shared {
//                            ledgerItems
//                        }
//                        col {
//                            forEach(items) { ledgerItem ->
//                                col {
//                                    spacing = AppDimensions.expandButtonSpace
//                                    // header row
//                                    sizeConstraints(minHeight = 2.rem) - BackgroundSemantic(AppColors.primary.main).onNext - ForegroundSemantic(
//                                        Color.white
//                                    ).onNext - row {
//                                        spacing = 0.px
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                        expanding - centered - bodyBold {
//                                            ::content { project?.name?.uppercase() ?: "No Project Found" }
//                                        }
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("SOURCE")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("STATUS")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("AVAILABLE")
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                    }
//                                    // breakdown rows
//                                    sizeConstraints(minHeight = 2.rem) - row {
//                                        spacing = 0.px
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                        expanding - centered - body("ledgerItem")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
//                                            if (ledgerItem.payApp != null) {
//                                                "Draw Payment"
//                                            } else if (ledgerItem.lineItem != null) {
//                                                "Work Item Approval"
//                                            } else {
//                                                "Retention Release"
//                                            }
//                                        )
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - colored(AppColors.secondary.main) - centered - bold - centeredBody(
//                                            "Available"
//                                        )
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
//                                            ledgerItem.amount.renderDollars(
//                                                true
//                                            )
//                                        )
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                    }
//                                    sizeConstraints(minHeight = 2.rem) - row {
//                                        spacing = 0.px
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                        expanding - centered - body("Escrow Fee")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody("1%")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody("Paid")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
//                                            (ledgerItem.amount * 0.01).toFloat().renderDollars()
//                                        )
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                    }
//                                    // summary row
//                                    sizeConstraints(minHeight = 2.rem) - lightSection - row {
//                                        spacing = 0.px
//                                        expanding - space()
//                                        sizeConstraints(minWidth = 6.rem) - centered - centeredBody("Available To Transfer")
//                                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody(
//                                            (ledgerItem.amount * 0.99).toFloat().renderDollars()
//                                        )
//                                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
//                                    }
//                                }
//                            }
//                        }
//                        // view ledger button
//                        gravity(Align.End, Align.Center) - textButton - button {
//                            colored(AppColors.primary.main) - subTitle("View Ledger")
//                            action = Action("View Project Ledger") {
//                                // todo: make this navigate to the project ledger as well
//                                val id = project?._id
//                                if (id != null) {
//                                    JumpTo.ProjectLedger()
//                                    screenNavigator.navigate(ContractorProject(id))
//                                }
//                            }
//                        }
//                    }
//                }
//            }
//            space()
//        }

        // display a list of available funds breakdown by project
        // organize the available funds and connected projects
        val rows2 = shared {
            newAvailableFunds().map { ledgerItem ->
                val project = projects().find { it._id == ledgerItem.project }
                Pair(project, ledgerItem)
            }
        }

        col {
            spacing = AppDimensions.buttonSpacing
            row {
                existsDefaultFalse { rows2().isEmpty() }
                body("No New Available Funds Found")
            }
            forEach(rows2) { (project, ledgerItem) ->
                col {
                    spacing = AppDimensions.expandButtonSpace
                    // header row
                    sizeConstraints(minHeight = 2.rem) - BackgroundSemantic(AppColors.primary.main).onNext - ForegroundSemantic(
                        Color.white
                    ).onNext - row {
                        spacing = 0.px
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                        expanding - centered - bodyBold {
                            ::content { project?.name?.uppercase() ?: "No Project Found" }
                        }
                        sizeConstraints(maxWidth = 9.rem) - expanding - centered - bold - centeredBody("SOURCE")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("STATUS")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("AVAILABLE")
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    }
                    // breakdown rows
                    sizeConstraints(minHeight = 2.rem) - row {
                        spacing = 0.px
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                        expanding - centered - body {
                            ::content {
                                lineItems().find { it._id == ledgerItem.lineItem }?.name ?: "[line item]"
                            }
                        }
                        sizeConstraints(maxWidth = 9.rem) - expanding - centered - centeredBody(
                            if (ledgerItem.payApp != null) {
                                "Draw Payment"
                            } else if (ledgerItem.lineItem != null) {
                                "Work Item Approval"
                            } else {
                                "Retention Release"
                            }
                        )
                        sizeConstraints(maxWidth = 7.rem) - expanding - colored(AppColors.secondary.main) - centered - bold - centeredBody(
                            "Available"
                        )
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                            ledgerItem.amount.renderDollars(
                                true
                            )
                        )
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    }
                    sizeConstraints(minHeight = 2.rem) - row {
                        spacing = 0.px
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                        expanding - centered - body("Escrow Fee")
                        sizeConstraints(maxWidth = 9.rem) - expanding - centered - centeredBody("1%")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody("Paid")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                            (ledgerItem.amount * 0.01).toFloat().renderDollars(true)
                        )
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    }
                    // summary row
                    sizeConstraints(minHeight = 2.rem) - lightSection - row {
                        spacing = 0.px
                        expanding - space()
                        sizeConstraints(minWidth = 6.rem) - centered - centeredBody("Available To Transfer")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody(
                            (ledgerItem.amount * 0.99).toFloat().renderDollars(true)
                        )
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    }
                    // view ledger button
                    gravity(Align.End, Align.Center) - textButton - button {
                        colored(AppColors.primary.main) - subTitle("View Ledger")
                        action = Action("View Project Ledger") {
                            val id = project?._id
                            if (id != null) {
                                JumpTo.ProjectLedger()
                                screenNavigator.navigate(ContractorProject(id))
                            }
                        }
                    }
                }
            }
        }
    }

    fun ViewWriter.transferHistoryTable(
        onlyShowRecent: Boolean = false
    ) = lightSection - col {
        spacing = AppDimensions.sectionIndent
        val expand = Property(true)
        space(0.0)
        if (onlyShowRecent) title("Recent Transfers") else
            col {
                spacing = 0.px
                sizeConstraints(height = 3.rem) - row {
                    expanding - button {
                        spacing = 0.px
                        row {
                            spacing = AppDimensions.expandButtonSpace
                            centered - expandIcon(expand)
                            centered - title("Transfer History")
                        }
                        action = expandAction(expand)
                    }
                }
            }

        val rows = shared {
            val items = ledgerItems().filter {
                it.amount < 0
            }.mapIndexed { index, item ->
                Pair(index, item)
            }
            if (onlyShowRecent) {
                items.take(5)
            } else items
        }

        fun ViewWriter.displayRows() {
            col {
                spacing = AppDimensions.buttonSpacing
                gravity(Align.End, Align.Center) - row {
//                    exists = !onlyShowRecent
                    exists = false
                    textButton - button {
                        ::enabled {
                            transferPage() > 0
                        }
                        icon(Icon.chevronLeft, "View Previous Page")
                        action = Action("View Previous Page") {
                            transferPage set (transferPage() - 1)
                        }
                    }
                    textButton - button {
                        ::enabled {
                            ledgerItemCount() > pageSize * (transferPage() + 1)
                        }
                        icon(Icon.chevronRight, "View Next Page")
                        action = Action("View Next Page") {
                            transferPage set (transferPage() + 1)
                        }
                    }
                    centered - smallBody {
                        ::content {
                            val startIndex = pageSize * transferPage()
                            val endIndex = startIndex + pageSize
                            "${startIndex + 1}-${if (endIndex + 1 < ledgerItemCount()) endIndex else ledgerItemCount()} of ${ledgerItemCount()}"
                        }
                    }
                }

                // header row
                sizeConstraints(minHeight = 2.rem) - BackgroundSemantic(AppColors.primary.main).onNext - ForegroundSemantic(
                    Color.white
                ).onNext - row {
                    spacing = 0.px
                    sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    if (onlyShowRecent) {
                        expanding - centered - bodyBold("ACCOUNT")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("DATE")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("DEPOSITED")
                    } else {
                        expanding - centered - bodyBold("ACCOUNT")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("DATE")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("METHOD")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("AMOUNT")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("FEE")
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("DEPOSITED")
                    }
                    sizeConstraints(maxWidth = 1.rem) - expanding - space()
                }

                // alternating row background color
                col {
                    spacing = AppDimensions.buttonSpacing
                    row {
                        existsDefaultFalse { rows().isEmpty() }
                        body("No Transfers Found")
                    }
                    col {
                        spacing = AppDimensions.buttonSpacing
                        forEach(rows) { (index, ledgerItem) ->
                            sizeConstraints(minHeight = 2.rem) - row {
                                themeChoice += if (index % 2 == 0) LightSection else TransparentBackgroundSemantic()
                                spacing = 0.px
                                sizeConstraints(maxWidth = 1.rem) - expanding - space()
                                if (onlyShowRecent) {
                                    expanding - centered - body("Transfer to ${ledgerItem.accountName ?: "[account name]"}")
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        ledgerItem.created.format(
                                            Formats.mmddyyyy
                                        )
                                    )
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        (ledgerItem.amount * 0.99).toFloat().renderDollars(true)
                                    )
                                } else {
                                    expanding - centered - body("Transfer to [account name]")
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        ledgerItem.created.format(
                                            Formats.mmddyyyy
                                        )
                                    )
                                    // currently 2-3 days is the only method
                                    sizeConstraints(maxWidth = 7.rem) - expanding - colored(AppColors.grey.main) - centered - centeredBody(
                                        ledgerItem.method?.name ?: "2-3 Day"
                                    )
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        ledgerItem.amount.renderDollars(
                                            true
                                        )
                                    )
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        (ledgerItem.amount * 0.01).toFloat().renderDollars(true)
                                    )
                                    sizeConstraints(maxWidth = 7.rem) - expanding - centered - centeredBody(
                                        (ledgerItem.amount * 0.99).toFloat().renderDollars(true)
                                    )
                                }
                                sizeConstraints(maxWidth = 1.rem) - expanding - space()
                            }
                        }
                    }
                    gravity(Align.End, Align.Center) - textButton - button {
                        exists = onlyShowRecent
                        colored(AppColors.primary.main) - subTitle("View Full Transfer History")
                        action = Action("View Full Transfer History") {
                            screenNavigator.reset(ContractorBillingInfo())
                        }
                    }
                }
            }
        }

        if (onlyShowRecent) onlyWhen { expand() } - col {
            displayRows()
        } else onlyWhen { expand() } - sectionIndentCol {
            displayRows()
        }
    }

    fun ViewWriter.billingHistoryTable() = lightSection - col {
        spacing = AppDimensions.sectionIndent
        val expand = Property(true)
        space(0.0)
        col {
            spacing = 0.px
            sizeConstraints(height = 3.rem) - row {
                expanding - button {
                    spacing = 0.px
                    row {
                        spacing = AppDimensions.expandButtonSpace
                        centered - expandIcon(expand)
                        centered - title("Billing History")
                    }
                    action = expandAction(expand)
                }
            }
        }

        // todo:
        // display a list of costs for the contractor
        // right now there is only escrow fees, or 1% on each transfer out
        // in the future will also have subscription fees and transfer fees when taking out money early

        val bills = shared {
            ledgerItems().filter {
                it.amount < 0
            }.mapIndexed { index, item ->
                Pair(index, item)
            }
        }

        onlyWhen { expand() } - sectionIndentCol {
            smallBody("CrowPay currently Collects 1% of transaction etc (Need a summary of what the billing page might expect")
            spacing = AppDimensions.buttonSpacing
            sizeConstraints(minHeight = 2.rem) - BackgroundSemantic(AppColors.primary.main).onNext - ForegroundSemantic(
                Color.white
            ).onNext - row {
                spacing = 0.px
                sizeConstraints(maxWidth = 1.rem) - expanding - space()
                sizeConstraints(maxWidth = 5.rem) - expanding - centered - bold - body("DATE")
                expanding - centered - bold - body("DESCRIPTION")
                sizeConstraints(maxWidth = 4.rem) - expanding - centered - bold - centeredBody("TYPE")
                sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("METHOD")
                sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody("AMOUNT")
                sizeConstraints(maxWidth = 1.rem) - expanding - space()
            }

            // alternating row background color
            col {
                spacing = AppDimensions.buttonSpacing
                row {
                    existsDefaultFalse { bills().isEmpty() }
                    body("No Billing History Found")
                }
                forEach(bills) { (index, ledgerItem) ->
                    sizeConstraints(minHeight = 2.rem) - row {
                        themeChoice += if (index % 2 == 0) LightSection else TransparentBackgroundSemantic()
                        spacing = 0.px
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                        sizeConstraints(maxWidth = 5.rem) - expanding - centered - bold - body(
                            ledgerItem.created.format(
                                Formats.mmddyyyy
                            )
                        )
                        expanding - centered - bold - body("Escrow Fee")
                        // currently 2-3 days is the only method
                        sizeConstraints(maxWidth = 4.rem) - expanding - colored(AppColors.grey.main) - centered - bold - centeredBody(
                            "1%"
                        )
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody(
                            "Hold Back"
                        )
                        sizeConstraints(maxWidth = 7.rem) - expanding - centered - bold - centeredBody(
                            (ledgerItem.amount * 0.01).toFloat().renderDollars(true)
                        )
                        sizeConstraints(maxWidth = 1.rem) - expanding - space()
                    }
                }
            }
        }
    }
}